/* oxlint-disable typescript/consistent-type-assertions, unicorn/no-useless-promise-resolve-reject */
import { toSearchParams } from './to-search-params';
import type { ClientInput, ClientOptions, CustomRequestInit, QueryValue } from './types';
import { ResponseError } from './response-error';

export type InternalClientOptions<TResponse> = ClientOptions<TResponse> & {
  fetch?: typeof fetch;
  headers?: Headers;
};

function parseInput(input: string | URL): URL {
  try {
    return new URL(input.toString());
  } catch {
    throw new Error(`Invalid URL: ${input.toString()}`);
  }
}

async function defaultParseResponse(response: Response): Promise<unknown> {
  if (response.status === 204) return Promise.resolve('');

  const data = await (response.clone().json() as Promise<unknown>).catch(() => response.text());

  return data ?? null;
}

function isGetRequest(requestInit: CustomRequestInit): boolean {
  return requestInit.method?.toUpperCase() === 'GET' || !requestInit.method;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && value.constructor === Object;
}

export const base = async <TResponse = unknown>(
  input: ClientInput,
  options?: InternalClientOptions<TResponse>
): Promise<TResponse> => {
  const {
    params,
    fetch = globalThis.fetch,
    parseResponse = defaultParseResponse,
    ...requestOptions
  } = options ?? {};

  let { body } = requestOptions;

  const url = parseInput(input);

  if (isGetRequest(requestOptions) && params) {
    // Proto toJSON() is typed as unknown; GET query params are snake_case plain objects at runtime.
    const searchParams = toSearchParams(params as Record<string, QueryValue>);

    searchParams.forEach((value, key) => {
      url.searchParams.append(key, value);
    });
  }

  if (isPlainObject(body)) {
    body = JSON.stringify(body);
  }

  const headers = requestOptions.headers ?? new Headers();

  if (typeof body === 'string') {
    headers.set('Content-Type', 'application/json');
  } else if (body instanceof FormData) {
    // Let the browser set the correct Content-Type with boundary
  } else if (body !== undefined && body !== null) {
    throw new Error('Body must be a string, object, or FormData');
  }

  const request = new Request(url, {
    credentials: 'include',
    ...requestOptions,
    headers,
    body: body as BodyInit | null | undefined,
  });

  const response = await fetch(request);

  if (!response.ok) {
    throw new ResponseError(response, await parseResponse(response), request);
  }

  return parseResponse(response) as Promise<TResponse>;
};
